import React, { useEffect, useRef, useCallback } from "react";
import { Calendar, CalendarOptions } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import hrLocale from "@fullcalendar/core/locales/hr";
import { router } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Doctor, Event } from "@/lib/types/models.ts";
import {mapAppEventToFullCalendarEvent} from "@/lib/mapUtils.ts";

interface AppointmentCalendarProps {
    events: Event[];
    doctors: Doctor[];
    onDateClick?: (start_at: string) => void;
    onEventClick?: (event: Event) => void;
    onCallListClick?: () => void;
    calendarRefCallback?: (calendar: Calendar) => void;
}

const dayShortSRLatin = ["ned", "pon", "uto", "sre", "čet", "pet", "sub"];

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
                                                                     events,
                                                                     doctors,
                                                                     onDateClick,
                                                                     onEventClick,
                                                                     onCallListClick,
                                                                     calendarRefCallback,
                                                                 }) => {
    const calendarRef = useRef<HTMLDivElement | null>(null);
    const calendarInstanceRef = useRef<Calendar | null>(null);
    const resizeObserverRef = useRef<any>(null);
    const { translations } = usePage().props as unknown as { translations: any };
    const t = translations.calendar;

    const saveCalendarState = useCallback((info: any) => {
        localStorage.setItem("lastCalendarView", info.view.type);

        let saveDate = info.start;
        if (info.view.type.toLowerCase().includes("month")) {
            const tmp = new Date(info.start.getTime());
            tmp.setDate(tmp.getDate() + 14);
            saveDate = tmp;
        }
        localStorage.setItem("lastCalendarDate", saveDate.toISOString());
    }, []);

    const setupResizeObserver = useCallback((calendar: Calendar) => {
        const RO = (window as any).ResizeObserver;
        if (RO && calendarRef.current) {
            const ro = new RO(() => {
                try { calendar.updateSize(); } catch {}
            });
            ro.observe(calendarRef.current);
            resizeObserverRef.current = ro;
        } else {
            const onResize = () => {
                try { calendar.updateSize(); } catch {}
            };
            window.addEventListener("resize", onResize);
            resizeObserverRef.current = onResize;
        }
    }, []);

    useEffect(() => {
        if (!calendarRef.current) return;

        const savedDate = localStorage.getItem("lastCalendarDate");
        const savedView = localStorage.getItem("lastCalendarView");
        const initialDate = savedDate ? new Date(savedDate) : undefined;

        const options: CalendarOptions = {
            expandRows: true,
            plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
            initialView: savedView || "timeGridWeek",
            initialDate,
            displayEventTime: false,
            hiddenDays: [0, 6],
            slotEventOverlap: false,
            headerToolbar: {
                left: "prev,next today callListButton",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
            },
            customButtons: {
                callListButton: { text: "Lista za pozivanje", click: () => onCallListClick?.() },
            },
            buttonText: {
                today: t.header.today,
                month: t.views.month,
                week: t.views.week,
                day: t.views.day,
                prev: "Prethodni",
                next: "Sledeći",
            },
            locale: hrLocale,
            slotMinTime: "08:00:00",
            slotMaxTime: "21:00:00",
            allDaySlot: false,
            selectable: true,
            editable: true,
            eventStartEditable: true,
            eventDurationEditable: true,
            snapDuration: "00:15:00",
            eventOverlap: true,
            dateClick: (info: DateClickArg) => onDateClick?.(info.dateStr),
            eventClick: (info) => {
                const clickedEvent = events.find((e) => e.id.toString() === info.event.id);
                if (clickedEvent) onEventClick?.(clickedEvent);
            },
            eventDrop: (info) => {
                const id = info.event.id;
                const start = info.event.start?.toISOString();
                const end = info.event.end?.toISOString() ?? null;
                if (!start) return info.revert();

                router.patch(
                    `/events/${id}`,
                    { event: { start_at: start, end_at: end } },
                    { preserveScroll: true, onError: () => info.revert() }
                );
            },
            eventResize: (info) => {
                const id = info.event.id;
                const start = info.event.start?.toISOString();
                const end = info.event.end?.toISOString();
                if (!start || !end) return info.revert();

                router.patch(
                    `/events/${id}`,
                    { event: { start_at: start, end_at: end } },
                    { preserveScroll: true, onError: () => info.revert() }
                );
            },
            eventDidMount: (info) => {
                if (info.event.start && info.event.end) {
                    const startTime = info.event.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                    const endTime = info.event.end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                    info.el.title = `${startTime} - ${endTime}`;
                }
            },
            views: {
                timeGridWeek: { titleFormat: { day: "numeric", month: "numeric", year: "numeric" } },
                timeGridDay: { titleFormat: { day: "numeric", month: "numeric", year: "numeric" } },
                dayGridMonth: { titleFormat: { month: "numeric", year: "numeric" } },
            },
            dayHeaderContent: (args) => {
                const dayIndex = args.date.getDay();
                if (args.view.type === "dayGridMonth") return dayShortSRLatin[dayIndex];
                const dateNum = args.date.getDate();
                const monthNum = args.date.getMonth() + 1;
                return `${dayShortSRLatin[dayIndex]} ${dateNum.toString().padStart(2, "0")}.${monthNum.toString().padStart(2, "0")}`;
            },
            datesSet: (info) => {
                const toolbar = calendarRef.current?.querySelector(".fc-toolbar-chunk");
                const btn = toolbar?.querySelector<HTMLButtonElement>(".fc-callListButton-button");
                if (btn) btn.style.display = info.view.type === "dayGridMonth" ? "inline-flex" : "none";

                saveCalendarState(info);
            },
        };

        const calendar = new Calendar(calendarRef.current, options);
        calendar.render();
        calendarInstanceRef.current = calendar;
        calendarRefCallback?.(calendar);

        calendar.removeAllEvents();
        events.map(e => mapAppEventToFullCalendarEvent(e, doctors)).forEach(e => calendar.addEvent(e));

        setupResizeObserver(calendar);

        return () => {
            if (resizeObserverRef.current) {
                if (typeof resizeObserverRef.current.disconnect === "function") {
                    resizeObserverRef.current.disconnect();
                } else {
                    window.removeEventListener("resize", resizeObserverRef.current);
                }
                resizeObserverRef.current = null;
            }
            calendar.destroy();
            calendarInstanceRef.current = null;
        };
    }, [events, mapAppEventToFullCalendarEvent, onCallListClick, onDateClick, onEventClick, t, calendarRefCallback, saveCalendarState, setupResizeObserver]);

    useEffect(() => {
        const cal = calendarInstanceRef.current;
        if (!cal) return;
        cal.removeAllEvents();
        events.map(e => mapAppEventToFullCalendarEvent(e, doctors)).forEach(e => cal.addEvent(e));
        try { cal.updateSize(); } catch {}
    }, [events, mapAppEventToFullCalendarEvent]);

    return <div ref={calendarRef} className="w-full h-full rounded-lg shadow-md" />;
};

export default AppointmentCalendar;